//////
////  COMPOSE AND CURRY
//

// identity :: a -> a
/** Just returns the argument (sometimes useful when using ifElse in a composition)*/
export function identity(a) {
  return a;
}

// compose :: ((y -> z), (x -> y), ..., (b -> c), (a... -> b)) -> (a... -> z)
export function compose(...fns) {
  return (x) => fns.reduceRight((y, fn) => fn(y), x);
}

// pipe :: ((a... -> b), (b -> c), ..., (x -> y), (y -> z)) -> (a… -> z)
export function pipe(...fns) {
  return (x) => fns.reduce((y, fn) => fn(y), x);
}

export const converge = (converger, fns) => (x) =>
  converger(...fns.map((fn) => fn(x)));

// curry :: (* -> a) -> (* -> a)
// Doesn't work with variadic functions!!! (...args) => ...
function curry(fn) {
  return function curried(...args) {
    return args.length < fn.length
      ? (...newArgs) => curried(...args, ...newArgs)
      : fn(...args);
  };
}

//////
////  POINT FREE ARRAY UTILS
//
// This all return functions expecting the array, so that they can be composed
// or piped. Should work with any functor implementing methods of the same name
// and signature.

// slice :: (Number, Number) -> [a] -> [a]
export function slice(start, end) {
  return (list) => list.slice(start, end);
}

// append :: a -> [a] -> [a]
export function append(item) {
  return (list) => [...list, item];
}

// sort :: ((a, a) -> Number) -> [a] -> [a] // Doens't mutate the original array
export function sort(compareFn) {
  return (list) => [...list].sort(compareFn);
}

// map :: (a -> b) -> [a] -> [b]
export function map(transform) {
  return (list) => list.map(transform);
}

// filter :: ( a -> Boolean ) -> [a] -> [a]
export function filter(predicate) {
  return (list) => list.filter(predicate);
}
// reduce :: ((a, b) -> a, a) -> [b] -> a
export function reduce(reducer, initial) {
  (list) => list.reduce(reducer, initial);
}

//////
//// VALUE UTILS
//

export function isNil(val) {
  return val == undefined || val == null ? true : false;
}
//////
//// OTHER ARRAY UTILS
//
// head :: [a] -> a
export function head([head]) {
  return head;
}

// last :: [a] -> a
export function last(list) {
  return list[list.length - 1];
}

// isEmpty :: [a] -> a
export function isEmpty(list) {
  return !!list.length;
}

// range :: (Number, Number) => [b]
export function range(min, max) {
  return [...Array(Math.abs(max - min))].map(
    (n, i) => min + Math.sign(max - min) * i
  );
}

// propAverage :: ( obj -> propVal ) -> [obj] -> Number
export const averageOverProp = (propExtractor) => (list) =>
  list.reduce((acc, obj) => acc + propExtractor(obj), 0) / list.length;

//////
////  Flow control
//

// ifElse :: (a -> Boolean, a -> b, a -> b) -> (a -> b)
export function ifElse(predicate, onTrue, onFalse) {
  return (val) => (predicate(val) ? onTrue(val) : onFalse(val));
}

// pattern matching (Not sure it composes)
// https://codeburst.io/alternative-to-javascripts-switch-statement-with-a-functional-twist-3f572787ba1c
const matched = (x) => ({
  on: () => matched(x),
  otherwise: () => x,
});
export const match = (x) => ({
  on: (pred, fn) => (pred(x) ? matched(fn(x)) : match(x)),
  otherwise: (fn) => fn(x),
});
/* Example
match(50)
  .on(x => x < 0, () => 0)
  .on(x => x >= 0 && x <= 1, () => 1)
  .otherwise(x => x * 10)
// => 500
*/
// Map value until de result satifies condition (Not sure it composes)
const done = (x) => ({
  attempt: () => done(x),
  finally: (fn) => fn(x),
});
export const until = (pred, x) => ({
  attempt: (fn) => {
    const y = fn(x);
    return pred(y) ? done(y) : until(pred, x);
  },
  finally: (_, fn) => fn(x),
});

//////
//// NOT IN Ramda
//
export function shuffle(array) {
  let currentIndex = array.length,
    randomIndex;

  // While there remain elements to shuffle.
  while (currentIndex != 0) {
    // Pick a remaining element.
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex--;

    // And swap it with the current element.
    [array[currentIndex], array[randomIndex]] = [
      array[randomIndex],
      array[currentIndex],
    ];
  }

  return array;
}

//////
//// LOGGING
//

export const log = curry(
  (tag, x) => (console.log(`\n[ ${tag || 'LOG'} ]\n`, x), x)
);
export const log2 = curry(
  (tag, x, y) => (console.log(`\n[ ${tag || 'LOG'} ]\n`, x, y), y)
);

const generateCompletedRecords = (miniType, ammount) => {
  const step = 2;
  return range(100, 100 - ammount)
    .map((x, i) => x - (step - 1) * i)
    .map((x, i) => ({
      updatedAt: '2022-06-07T11:01:20Z',
      data: {
        miniactivityType: miniType,
        progress: 100,
        correct: x - 10,
      },
    }));
};
const generateIncompletedRecords = (miniType, ammount, fromPercent) => {
  const step = 2;
  return range(fromPercent, fromPercent - ammount)
    .map((x, i) => x - (step - 1) * i)
    .map((x, i) => ({
      updatedAt: '2022-06-07T11:01:20Z',
      data: {
        miniactivityType: miniType,
        progress: i % 2 == 1 ? x + 2 : x,
        correct: x - 2,
      },
    }));
};
